import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table,
  Dimmer,
  Segment,
  Loader,
  Dropdown,
  Menu
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import { iRootState, iRootDispatch } from '../../store';
import { DateToStringDate } from '../../services/date';
import { EstateState } from '../../types/EstateState';
import {
  getEstatesObjectsFromGetEstatesDTO,
  getEstatesByPage,
  useDropDownUsers
} from '../../services/broker';
import IconButton from '../../components/IconButton';
import { maskCnpj, unmaskCpfOrCnpj } from '../../services/masks';
import { UserState } from '../../types/UserState';
import { EstateStatusEnum } from '../../enums/estate-status.enum';
import {
  UserForUpdateEstateForm,
  companyOptions,
  stateOptions,
  statusList,
  typeOptions
} from './types';
import { dangerNotification } from '../../services/notification';
import { UsersToCustomDropDownOptions } from '../../util';
import { CustoDropDownItemProps } from '../../components/types';
import AuthorizationContainer, {
  hasPermission
} from '../../components/AuthorizationContainer';
import { ActionEnum } from '../../enums/authz-action.enum';
import { FeatureEnum } from '../../enums/authz-feature.enum';
import { Header, TableWrapper, NotFoundWrapper } from './styles';
import { getEstateToView } from '../../services/estate';
import { GetEstateToViewDTO } from '../../dtos/estate/estate-for-get-estates-response.dto';
import ViewModal from './components/ViewModal';
import { ClickableTableCell } from '../../styles';
import FollowUpModal from '../../components/GeneralFollowUpModal';
import TablePaginationMenu from '../../components/TablePaginationMenu';
import Button from '../../components/Button';
import FilterBar, { FilterField, FilterValues, toDateOnlyString, toFilterIdList } from '../../components/Filter';

type SearchField = 'document' | 'name';

type ApiFilters = {
  name?: string;
  document?: string;
  state?: string;
  status?: string | string[];
  user_id?: string;
  franchise_id?: string;
  responsible_company?: string;
  start?: string;
  end?: string;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name'];

const STATUS_OPTIONS = statusList.filter(item => item.value !== 'ALL');
const COMPANY_OPTIONS = companyOptions.filter(item => item.value !== 'ALL');

const initialFilterValues: FilterValues = {
  type: 'document',
  argument: '',
  state: '',
  start: '',
  end: '',
  status: [],
  franchise_id: '',
  user_id: '',
  responsible_company: ''
};

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  const dateOnly = toDateOnlyString(value);
  return dateOnly || undefined;
};

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = (values.type as SearchField) || 'document';
  let argument = String(values.argument || '').trim();

  if (searchType === 'document' && argument) {
    argument = unmaskCpfOrCnpj(argument);
  }

  const filter: ApiFilters = {};

  if (argument && SEARCH_FIELDS.includes(searchType)) {
    filter[searchType] = argument;
  }

  if (values.state) filter.state = String(values.state);

  const start = formatDateValue(values.start);
  const end = formatDateValue(values.end);
  if (start) filter.start = start;
  if (end) filter.end = end;

  const statuses = toFilterIdList(values.status)?.filter(status => status !== 'ALL');
  if (statuses?.length) {
    filter.status = statuses;
  }

  if (values.franchise_id) filter.franchise_id = String(values.franchise_id);
  if (values.user_id) filter.user_id = String(values.user_id);

  if (values.responsible_company && values.responsible_company !== 'ALL') {
    filter.responsible_company = String(values.responsible_company);
  }

  return filter;
};

const mapState = (state: iRootState) => ({
  estatesList: state.estatesList,
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateEstatesList: (payload: any) => dispatch.estatesList.updateEstatesList(payload)
});

type Props = { currentPage: number; setCurrentPage: (page: number) => void } & ReturnType<
  typeof mapState
> &
  ReturnType<typeof mapDispatch>;

const EstatesTable = (props: Props) => {
  const { currentPage, setCurrentPage } = props;
  const user: UserState = props.user;
  const { isLoading, estates } = props.estatesList;
  const [dataCount, setDataCount] = useState<number>(0);
  const [accountManagerOptions, setAccountManagerOptions] = useState<
    CustoDropDownItemProps[]
  >([]);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [estateSelected, setEstateSelected] = useState<GetEstateToViewDTO | null>(null);
  const [openFollowUp, setOpenFollowUp] = useState<boolean>(false);
  const [followUpId, setFollowUpId] = useState<string>('');
  const [userCanSeeDetails, setUserCanSeeDetails] = useState<boolean>(false);

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    const has = hasPermission(ActionEnum.seeDetails, FeatureEnum.estates);
    setUserCanSeeDetails(has);
  }, []);

  const brokerId = user?.broker?.id || 'test';
  const isEstateUser = Boolean(user.role?.includes('ESTATE'));

  const {
    data: usersData,
    isLoading: isAccountManagerLoading,
    isError
  } = useDropDownUsers(brokerId);

  useEffect(() => {
    if (isError) {
      dangerNotification(
        'Oops...',
        'Não foi possível encontrar os analistas disponíveis'
      );
    }

    if (usersData && !isError) {
      const convertedValues: UserForUpdateEstateForm[] = usersData.map((userItem: any) => ({
        id: userItem.id,
        name: userItem.name
      }));

      const analystsOptions = UsersToCustomDropDownOptions(false, convertedValues);
      setAccountManagerOptions(analystsOptions);
    }
  }, [usersData, isError]);

  const franchiseOptions = useMemo(
    () =>
      (props.availableBrokersAndEstates?.franchises || []).map(item => ({
        value: String(item.value || ''),
        text: String(item.text || '')
      })),
    [props.availableBrokersAndEstates?.franchises]
  );

  const accountManagerSelectOptions = useMemo(
    () =>
      accountManagerOptions.map(item => ({
        value: String(item.value || ''),
        text: String(item.text || '')
      })),
    [accountManagerOptions]
  );

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'state',
        label: 'UF',
        placeholder: 'Selecionar...',
        options: toSelectOptions(stateOptions),
        getDisplayValue: values => {
          const option = stateOptions.find(
            item => String(item.value) === String(values.state)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'multiSelect',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(STATUS_OPTIONS)
      },
      {
        kind: 'select',
        name: 'franchise_id',
        label: 'Parceiro',
        placeholder: 'Selecionar...',
        advanced: true,
        options: toSelectOptions(franchiseOptions),
        getDisplayValue: values => {
          const option = franchiseOptions.find(
            item => String(item.value) === String(values.franchise_id)
          );
          return option ? option.text : undefined;
        }
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'user_id',
        label: 'Gestor de Contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagerLoading,
        options: toSelectOptions(accountManagerSelectOptions),
        getDisplayValue: values => {
          const option = accountManagerSelectOptions.find(
            item => String(item.value) === String(values.user_id)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push({
      kind: 'select',
      name: 'responsible_company',
      label: 'Responsável',
      placeholder: 'Selecionar...',
      advanced: true,
      options: toSelectOptions(COMPANY_OPTIONS),
      getDisplayValue: values => {
        const option = COMPANY_OPTIONS.find(
          item => String(item.value) === String(values.responsible_company)
        );
        return option ? option.text : undefined;
      }
    });

    return fields;
  }, [
    isEstateUser,
    franchiseOptions,
    accountManagerSelectOptions,
    isAccountManagerLoading
  ]);

  const getData = useCallback(async () => {
    try {
      props.updateEstatesList({ isLoading: true, estates: [] });
      const request = (await getEstatesByPage(
        user?.broker?.id || 'test',
        currentPage,
        appliedFiltersRef.current
      )) as any;
      const DTO = request.data;
      if (currentPage === 1) setDataCount(request.count);

      const data = getEstatesObjectsFromGetEstatesDTO(DTO);

      const dataFormatted = data.map(d => {
        let status = 'Pendente';
        switch (d.status) {
          case EstateStatusEnum.blocked:
            status = 'Bloqueado';
            break;
          case EstateStatusEnum.inactive:
            status = 'Inativo';
            break;
          case EstateStatusEnum.pending:
            status = 'Pendente';
            break;
          case EstateStatusEnum.active:
            status = 'Ativo';
            break;
          case EstateStatusEnum.update:
            status = 'Update';
            break;
        }
        return { ...d, status };
      });

      props.updateEstatesList({
        isLoading: false,
        estates: dataFormatted || []
      });
    } catch (err) {
      dangerNotification('Oops...', 'Não foi possível encontrar as imobiliárias!');
      props.updateEstatesList({ isLoading: false, estates: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, user?.broker?.id]);

  useEffect(() => {
    getData();
  }, [currentPage, appliedFilters, getData]);

  const handleFilterSearch = (values: FilterValues) => {
    setAppliedFilters(normalizeToApiFilter(values));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setCurrentPage(1);
  };

  const trigger = (
    <>
      <IconButton name="ellipsis vertical" />
    </>
  );

  const handleFollowUp = (id: string) => {
    setFollowUpId(id);
    setOpenFollowUp(true);
  };

  const handleOpen = () => {
    setOpenFollowUp(true);
  };

  const handleClose = () => {
    setOpenFollowUp(false);
  };

  async function handleOpenViewModal(estateId: string) {
    const response = await getEstateToView(estateId);
    setEstateSelected(response);
    setIsViewModalOpen(true);
  }

  function renderTableData(estatesList: EstateState[], tableProps: any) {
    return estatesList.map(estate => {
      return (
        <Table.Row key={estate.id} style={{ cursor: userCanSeeDetails && 'pointer' }}>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {DateToStringDate(estate.createdAt || new Date(0, 0, 0))}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {estate.name}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {estate.status}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {maskCnpj(estate.cnpj)}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {estate.estateCity}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {estate.estateState}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {estate.user.name}
          </ClickableTableCell>
          <ClickableTableCell
            onClick={() => {
              if (userCanSeeDetails) {
                handleOpenViewModal(estate.id || '');
              }
            }}
          >
            {estate?.franchise?.name}
          </ClickableTableCell>
          <Table.Cell width={1} textAlign={'right'}>
            <Menu compact>
              <AuthorizationContainer
                action={ActionEnum.update}
                feature={FeatureEnum.estates}
              >
                <Menu.Item>
                  <Dropdown trigger={trigger} icon={null} direction="left">
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleOpenViewModal(estate.id || '')}>
                        Visualizar imobiliária
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() =>
                          tableProps.history.push('/admin/estate-edit/' + estate.id)
                        }
                      >
                        Editar Imobiliária
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() =>
                          tableProps.history.push(
                            '/admin/change-estate-email/' + estate.id,
                            estate
                          )
                        }
                      >
                        Alterar e-mail
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Menu.Item>
              </AuthorizationContainer>
              <Menu.Item>
                <Button
                  primary
                  icon="chat"
                  onClick={() => {
                    handleFollowUp(estate?.id ? estate?.id : '');
                  }}
                />
              </Menu.Item>
            </Menu>
          </Table.Cell>
        </Table.Row>
      );
    });
  }

  return (
    <>
      {estateSelected && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          estate={estateSelected}
        />
      )}
      {openFollowUp && (
        <FollowUpModal
          isOpen={openFollowUp}
          onOpen={handleOpen}
          onClose={handleClose}
          header="Follow Up"
          id={followUpId}
          context={'ESTATES'}
          user={user}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <FilterBar
          fields={filterFields}
          initialValues={initialFilterValues}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="estates"
        />
      </div>

      <Dimmer.Dimmable as={Segment} dimmed={isLoading}>
        <Dimmer active={isLoading} inverted>
          <Loader />
        </Dimmer>
        {estates && estates.length > 0 ? (
          <TableWrapper>
            <Table>
              <Header>
                <Table.Row>
                  <Table.HeaderCell>CRIADO EM</Table.HeaderCell>
                  <Table.HeaderCell>NOME</Table.HeaderCell>
                  <Table.HeaderCell>STATUS</Table.HeaderCell>
                  <Table.HeaderCell>CNPJ</Table.HeaderCell>
                  <Table.HeaderCell>CIDADE</Table.HeaderCell>
                  <Table.HeaderCell>UF</Table.HeaderCell>
                  <Table.HeaderCell>GESTOR DE CONTAS</Table.HeaderCell>
                  <Table.HeaderCell>PARCEIRO</Table.HeaderCell>
                  <Table.HeaderCell>AÇÕES</Table.HeaderCell>
                </Table.Row>
              </Header>
              <Table.Body>{renderTableData(estates, props)}</Table.Body>
              <Table.Footer>
                <Table.Row>
                  <Table.HeaderCell colSpan="17">
                    <TablePaginationMenu
                      currentPage={currentPage}
                      dataCount={dataCount}
                      setCurrentPage={setCurrentPage}
                    />
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Footer>
            </Table>
          </TableWrapper>
        ) : (
          <NotFoundWrapper>
            <p>Nenhuma imobiliária cadastrada</p>
          </NotFoundWrapper>
        )}
      </Dimmer.Dimmable>
    </>
  );
};

export default connect(mapState, mapDispatch)(EstatesTable);
